<script setup lang="ts">
import { ref, toRefs, provide, markRaw, effectScope, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import vtkInteractorStyleManipulator from '@kitware/vtk.js/Interaction/Style/InteractorStyleManipulator';
import { useVtkView } from '@/src/core/vtk/useVtkView';
import { useImage } from '@/src/composables/useCurrentImage';
import { useVtkInteractorStyle } from '@/src/core/vtk/useVtkInteractorStyle';
import { LPSAxisDir } from '@/src/types/lps';
import { useResizeObserver, watchImmediate } from '@vueuse/core';
import { resetCameraToImage, resizeToFitImage } from '@/src/utils/camera';
import { usePersistCameraConfig } from '@/src/composables/usePersistCameraConfig';
import { useAutoFitState } from '@/src/composables/useAutoFitState';
import { Maybe } from '@/src/types';
import { VtkViewApi } from '@/src/types/vtk-types';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useViewCameraStore } from '@/src/store/view-configs/camera';
import { useVtkInteractionManipulator } from '@/src/core/vtk/useVtkInteractionManipulator';
import vtkMouseCameraTrackballPanManipulator from '@kitware/vtk.js/Interaction/Manipulators/MouseCameraTrackballPanManipulator';
import vtkGestureCameraManipulator from '@kitware/vtk.js/Interaction/Manipulators/GestureCameraManipulator';

interface Props {
  viewId: string;
  imageId: Maybe<string>;
  viewDirection: LPSAxisDir;
  viewUp: LPSAxisDir;
}

const props = defineProps<Props>();
const {
  viewId: viewID,
  imageId: imageID,
  viewDirection,
  viewUp,
} = toRefs(props);

const vtkContainerRef = ref<HTMLElement>();
const viewCameraStore = useViewCameraStore();
const { disableCameraAutoReset } = storeToRefs(viewCameraStore);

const { metadata: imageMetadata } = useImage(imageID);

// use a detached scope so that actors can be removed from
// the renderer before the renderer is deleted.
const scope = effectScope(true);
const view = scope.run(() => useVtkView(vtkContainerRef))!;
onUnmounted(() => {
  scope.stop();
});

view.renderer.setBackground(0, 0, 0);
view.renderer.getActiveCamera().setParallelProjection(true);

const { interactorStyle } = useVtkInteractorStyle(
  vtkInteractorStyleManipulator,
  view
);
let panManipulator: any = null;
let gestureManipulator: any = null;

function setupAutoInteractions() {
  panManipulator = useVtkInteractionManipulator(
    interactorStyle,
    vtkMouseCameraTrackballPanManipulator,
    { button: 1 }
  );

  gestureManipulator = useVtkInteractionManipulator(
    interactorStyle,
    vtkGestureCameraManipulator,
    {}
  );
}

function disableAutoInteractions() {
  console.log('🔒 Disabling auto interactions');
  interactorStyle.removeAllMouseManipulators();
  interactorStyle.removeAllGestureManipulators();
}

function enableAutoInteractions() {
  console.log('🔓 Enabling auto interactions');
  setupAutoInteractions();
}

setupAutoInteractions();

if (typeof window !== 'undefined') {
  window.disableAutoInteractions = disableAutoInteractions;
  window.enableAutoInteractions = enableAutoInteractions;
}

// bind slice and window configs
// resizeToFit camera controls
const autoFit = useAutoFitState(view.renderer.getActiveCamera());

function autoFitImage() {
  if (!autoFit.autoFit.value) return;
  autoFit.withPaused(() => {
    resizeToFitImage(
      view,
      imageMetadata.value,
      viewDirection.value,
      viewUp.value
    );
  });
}

useResizeObserver(vtkContainerRef, () => {
  if (disableCameraAutoReset.value) return;
  autoFitImage();
});

function resetCamera() {
  autoFit.autoFit.value = true;
  autoFit.withPaused(() => {
    resetCameraToImage(
      view,
      imageMetadata.value,
      viewDirection.value,
      viewUp.value
    );
    autoFitImage();
  });
}

watchImmediate(
  [viewID, imageID, disableCameraAutoReset],
  ([viewID_, imageID_, noAutoReset]) => {
    if (
      imageID_ &&
      !viewCameraStore.isCameraInitialized(viewID_, imageID_) &&
      !noAutoReset
    ) {
      resetCamera();
      viewCameraStore.markCameraAsInitialized(viewID_, imageID_);
    }
  }
);

watchImmediate([imageMetadata, disableCameraAutoReset], () => {
  if (!imageMetadata.value) return;
  if (
    viewCameraStore.isCameraInitialized(viewID.value, imageID.value) ||
    disableCameraAutoReset.value
  ) {
    view.renderer.resetCameraClippingRange(imageMetadata.value.worldBounds);
  }
});

// persistent camera config
usePersistCameraConfig(viewID, imageID, view.renderer.getActiveCamera());

// exposed API
const api: VtkViewApi = markRaw({
  ...view,
  interactorStyle,
  resetCamera,
});

defineExpose(api);
provide(VtkViewContext, api);

function onPointerDown() {
  autoFit.resume();
}

function onPointerUp() {
  autoFit.pause();
}
</script>

<template>
  <div>
    <div
      ref="vtkContainerRef"
      class="view"
      @pointerdown.capture="onPointerDown"
      @pointerup.capture="onPointerUp"
    />
    <slot></slot>
  </div>
</template>

<style scoped>
.view {
  width: 100%;
  height: 100%;
}
</style>
